<template>
  <div
    v-if="freeSamples.active && cartCount > 0"
    class=""
  >
    <div class="accordion-item border-b">
      <h3>
        <button
          :aria-expanded="showFreeSamples ? 'true' : 'false'"
          aria-controls="free-samples-panel"
          id="free-samples-header"
          class="flex items-center justify-between w-full text-left py-4  cursor-pointer"
          :disabled="(cartTotal / 100) < activeTierMinPrice"
          @click="handleFreeSamplesToggle"
          @keydown="handleKeyDown"
        >
          <div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left flex-wrap">
            <span class="flex items-center relative gap-2">
              <span
                v-if="showAccordionIcons"
                class="mini-cart__util-icon"
              >
                <MiniCartIconGift />
              </span>
              {{ freeSamples.heading || 'Free Samples' }} <span class="text-xs">{{ sampleStatus.remainingText }}</span>
            </span>

            <span
              v-if="(cartTotal / 100) < activeTierMinPrice"
              class="text-xs font-normal text-gray-500"
            >
              {{ amountAwayMessage }}
            </span>

            <span
              v-else
              class="mini-cart__truncated-preview-text max-w-full!"
            >
              {{ eligibleText }}
            </span>
          </div>
          <svg
            :class="showFreeSamples ? 'rotate-180' : 'rotate-0'"
            class="w-4 h-4 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </button>
      </h3>
      <button
        v-if="showFreeSamples"
        @click="handleFreeSamplesToggle"
        class="w-full h-full fixed inset-0 mini-cart__blocker"
        style="
          background-color: var(--global-mask-color);
          backdrop-filter: var(--global-mask-blur);
        "
      >
        <span class="sr-only">close</span>
      </button>
      <div
        id="free-samples-panel"
        aria-labelledby="free-samples-header"
        class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel"
        :class="showFreeSamples ? 'max-h-96 opacity-100 panel_active' : 'panel_disabled'"
        @keydown="handlePanelKeyDown"
      >
        <section
          class="mini-cart__free-samples-container relative transition-all duration-300 ease-in-out pb-4"
          :class="showFreeSamples ? 'opacity-100' : 'opacity-0'"
        >
          <div class="flex justify-between items-center mb-2">
            <h3>{{ freeSamples.heading || 'Free Samples' }} <span>{{ sampleStatus.remainingText
                }}</span></h3>
            <button
              class="w-11 h-11 right-0 flex items-center justify-center"
              @click.passive="handleFreeSamplesToggle"
            >
              <MiniCartIconClose />
              <span class="sr-only">Close</span>
            </button> 
          </div>
          <div>
            <p
              v-if="!canAddMoreSamples && freeSamples.reachedLimitErrorMessage && addedSamples.length > 0"
              class="text-xs"
            >
              {{ freeSamples.reachedLimitErrorMessage }}
            </p>
            <p
              v-else-if="!eligibleTier"
              class="text-xs"
            >
              {{ freeSamples.description }}
            </p>
            <div
              v-if="eligibleTier && cartTotal >= eligibleTier.minimum_price && !(!canAddMoreSamples && freeSamples.reachedLimitErrorMessage && addedSamples.length > 0)"
              class="mini-cart__free-samples-message mb-2 text-xs"
            >
              {{ sampleStatus.message }}
            </div>
          </div>

          <div
            v-if="eligibleTier && canAddMoreSamples"
            class="
              mini-cart__free-samples-inner
              relative
            "
          >
            <Swiper
              :slides-per-view="2.5"
              :space-between="10"
              :observer="true"
              :observe-parents="true"
              :modules="modules"
              :threshold="2"
              :navigation="{
                nextEl: '.free-samples-swiper-button-next',
                prevEl: '.free-samples-swiper-button-prev',
              }"
              class="mini-cart__free-samples-swiper"
            >
              <SwiperSlide
                v-for="product in eligibleTierProducts"
                :key="product.id"
              >
                <div
                  class="
                    mini-cart__free-samples-item
                    border flex items-center gap-3
                    p-4
                    h-full
                  "
                >
                  <div class="mini-cart__free-samples-fig">
                    <img
                      :src="product.featured_image"
                      :alt="product.title"
                      class="h-10 w-10 border"
                      width="40"
                      height="40"
                      loading="lazy"
                    />
                  </div>

                  <div
                    class="mini-cart__free-samples-meta text-[0.875rem] flex-1 flex flex-col justify-between text-center">
                    <h2
                      class="mini-cart__meta-title matter-regular text-[0.875rem] leading-[1.08] pb-2 text-wrap"
                      :title="product.title"
                    >
                      {{ product.title }}
                    </h2>

                    <span class="mini-cart__meta-price text-[0.75rem] pb-1 leading-[1.25] hidden">
                      {{ currencySymbol }}
                      {{ (product.price / 100).toFixed(2) }}
                      <span
                        v-if="product.variants?.[0]?.compare_at_price > product.variants?.[0]?.price"
                        class="pl-1 inline-block line-through text-[#AE7273]"
                      >
                        {{ currencySymbol }}
                        {{ (product.variants[0].compare_at_price / 100).toFixed(2) }}
                      </span>
                    </span>
                    <form @submit.prevent="addToCart(product)">
                      <button
                        v-if="!productAlreadyAdded(product.id)"
                        type="submit"
                        name="add"
                        class="mini-cart__free-samples-add-btn mini-cart__button"
                        :disabled="isLoading(product.id) || isAnyLoading()"
                        :class="{ 'cursor-not-allowed': isLoading(product.id) }"
                      >
                        {{ isLoading(product.id) ? freeSampleAddingText : freeSampleAddButton }}
                      </button>

                      <p
                        v-else
                        class="text-[0.75rem] leading-[1.25] mb-4 py-1 text-center"
                      >
                        Product already added. Select a different free tier sample.
                      </p>
                    </form>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
            <div class="free-samples-swiper-button-prev swiper-button-prev"></div>
            <div class="free-samples-swiper-button-next swiper-button-next"></div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useCartLoading } from './composables/useCartLoading';
import { useAccordion } from './composables/useAccordion';
import MiniCartIconGift from './mini-cart--icon-gift.vue';
import MiniCartIconClose from './mini-cart--icon-close.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
// Simple debounce utility
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

export default {
  name: 'FreeSample',
  components: {
    MiniCartIconGift,
    MiniCartIconClose,
    Swiper,
    SwiperSlide,
  },
  props: {
    cartTotal: {
      type: Number,
      required: true,
    },
    cartCount: {
      type: Number,
      required: true,
    },
    freeSampleRef: {
      type: Object,
      default: () => ({}),
    },
    currencySymbol: {
      type: String,
      required: true,
      default: () => window.miniCartCustomSettings?.currencySymbol || '$',
    },
  },
  setup(props, { emit }) {
    const { setLoading, clearLoading, isLoading, isAnyLoading } = useCartLoading();
    // Swiper modules
    const modules = [Navigation];
    // Initialize reactive state
    const freeSamples = ref({ active: false, heading: '', description: '', tiers: [] });
    const addedSamples = ref([]);
    const cartItems = ref([]);
    const isInitialLoad = ref(true);
    const recentlyAddedSample = ref(null); // Track recently added sample to prevent immediate removal
    const eligibleText = ref(window.freeSamples?.eligibleText || "You're eligible for FREE Samples");
    const freeSampleAddingText = window.miniCartCustomSettings?.freeSampleAddingText || 'Adding...';
    const freeSampleAddButton = window.miniCartCustomSettings?.freeSampleAddButton || 'Add';
    const freeSampleAmountAwayText = window.miniCartCustomSettings?.freeSampleAmountAwayText || "You're {{ amount }} away from FREErrrrrrr Samples";
    const showAccordionIcons = ref(window.miniCartCustomSettings?.showAccordionIcons ?? true);

    // Accordion state
    // const showFreeSamples = ref(false);
    const { isOpen: showFreeSamples, toggle: toggleFreeSamples, close: closeFreeSamples } = useAccordion('free-samples');

    // Optimized eligibleTier computation
    const eligibleTier = computed(() => {
      if (!freeSamples.value.tiers?.length) return null;
      const cartValue = props.cartTotal / 100;
      const sorted = freeSamples.value.tiers.sort((a, b) => b.minimum_price - a.minimum_price);
      const activeTier = sorted.find(tier => cartValue >= tier.minimum_price);
      return activeTier || null;
    });

    const sortedTiers = computed(() => {
      return freeSamples.value.tiers ? [...freeSamples.value.tiers].sort((a, b) => a.minimum_price - b.minimum_price) : [];
    });

    const activeTierMinPrice = computed(() => {
      const cartValue = props.cartTotal / 100;
      if (eligibleTier.value) return eligibleTier.value.minimum_price;
      const currentTier = sortedTiers.value.find(tier => cartValue >= tier.minimum_price);
      if (currentTier) return currentTier.minimum_price;
      const nextTier = sortedTiers.value.find(tier => cartValue < tier.minimum_price);
      return nextTier ? nextTier.minimum_price : 0;
    });
    // Simplified tier message computation
    const sampleStatus = computed(() => {
      if (!eligibleTier.value) return { message: '', remainingText: '' };

      const totalSamples = eligibleTier.value.quantity;
      const addedCount = addedFromEligibleTier.value;
      const remaining = Math.max(totalSamples - addedCount, 0);

      return {
        message: `You may choose ${totalSamples} Free Samples (${remaining} left)`,
        remainingText: `(${remaining} free samples left)`
      };
    });

    // Computed message for amount away from free samples
    const amountAwayMessage = computed(() => {
      const amountNeeded = (activeTierMinPrice.value - (props.cartTotal / 100)).toFixed(2);
      return freeSampleAmountAwayText.replace(/{amount}/g, `$${amountNeeded}`);
    });


    // Filter eligible products
    const eligibleTierProducts = computed(() => {
      return eligibleTier.value?.products.filter(product => !addedSamples.value.includes(product.id)) || [];
    });

    // Count added samples from the eligible tier
    const addedFromEligibleTier = computed(() => {
      if (!eligibleTier.value) return 0;
      return addedSamples.value.filter(id => eligibleTier.value.products.some(p => p.id === id)).length;
    });

    // Check if more samples can be added
    const canAddMoreSamples = computed(() => {
      if (!eligibleTier.value) return false;
      return addedFromEligibleTier.value < eligibleTier.value.quantity;
    });

    // Check if product is already added
    const productAlreadyAdded = (productId) => addedSamples.value.includes(productId);

    // Check if product is a free sample
    const isFreeSample = (productId) =>
      freeSamples.value.tiers.some(tier => tier.products.some(product => product.id === productId));

    // Fetch cart and update samples
    const getCartAndUpdateSamples = async () => {
      try {
        const response = await fetch(`${Shopify.routes.root}cart.js`);
        if (!response.ok) throw new Error('Failed to fetch cart');
        const cartData = await response.json();
        cartItems.value = cartData.items;
        addedSamples.value = cartData.items
          .filter(item => isFreeSample(item.product_id || item.id))
          .map(item => item.product_id || item.id);
        emit('cart-updated', cartData);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    };

    // Remove item from cart
    const removeFromCart = async (itemKey) => {
      try {
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: itemKey, quantity: 0 }),
        });
        if (!response.ok) throw new Error('Failed to remove item');
        await getCartAndUpdateSamples();
      } catch (error) {
        console.error('Error removing item:', error);
      }
    };

    // Notify sample removal
    const notifySampleRemoved = (productId) => {
      if (!freeSamples.value?.tiers) return;
      for (const tier of freeSamples.value.tiers) {
        const targetSample = tier.products.find(product => product.id === productId);
        if (targetSample) {
          addedSamples.value = addedSamples.value.filter(id => id !== productId);
          break;
        }
      }
    };

    // Add product to cart
    const addToCart = async (product) => {
      if (!canAddMoreSamples.value || isAnyLoading()) {
        console.warn('Cannot add more samples; tier limit reached or already adding');
        return;
      }
      const variantId = product.variants.find(variant => variant.available)?.id;
      if (!variantId) {
        console.error('No available variant for product:', product.title);
        return;
      }
      try {
        setLoading(product.id);
        recentlyAddedSample.value = product.id;
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { _sample: true },
          }),
        });
        if (!response.ok) throw new Error('Failed to add to cart');
        await getCartAndUpdateSamples();
        setTimeout(() => { recentlyAddedSample.value = null; }, 500); // Extended protection window
      } catch (error) {
        console.error('Error adding to cart:', error);
        recentlyAddedSample.value = null;
      } finally {
        clearLoading();
      }
    };

    // Helper function to remove samples from ineligible tiers
    const removeIneligibleTierSamples = async (newTotal) => {
      if (!freeSamples.value?.tiers) return;
      const cartValue = newTotal / 100;
      console.log('Checking ineligible tiers:', { cartValue, addedSamples: addedSamples.value });
      for (const tier of freeSamples.value.tiers) {
        if (cartValue < tier.minimum_price) {
          const tierSampleIds = tier.products.map(product => product.id);
          for (const sampleId of addedSamples.value) {
            if (sampleId === recentlyAddedSample.value) {
              console.log('Skipping recently added sample:', sampleId);
              continue;
            }
            if (tierSampleIds.includes(sampleId)) {
              console.log('Removing sample from ineligible tier:', sampleId, tier.minimum_price);
              const itemToRemove = cartItems.value.find(item => (item.product_id || item.id) === sampleId);
              if (itemToRemove) {
                await removeFromCart(itemToRemove.key);
              }
            }
          }
        }
      }
    };

    // Debounced watcher to handle cart changes
    const debouncedRemoveIneligible = debounce(async (newTotal, oldTotal) => {
      if (isInitialLoad.value || newTotal === oldTotal) {
        return;
      }
      const cartValue = newTotal / 100;
      const minPrice = activeTierMinPrice.value;
      
      if (cartValue >= minPrice) {
        return;
      }
      
      await removeIneligibleTierSamples(newTotal);
      const eligibleSampleIds = freeSamples.value.tiers
        .filter(tier => newTotal >= tier.minimum_price * 100)
        .flatMap(tier => tier.products.map(product => product.id));
      console.log('Eligible sample IDs:', eligibleSampleIds);
      for (const sampleId of addedSamples.value) {
        if (sampleId === recentlyAddedSample.value) {
          console.log('Skipping recently added sample in cleanup:', sampleId);
          continue;
        }
        if (!eligibleSampleIds.includes(sampleId)) {
          console.log('Removing ineligible sample:', sampleId);
          const itemToRemove = cartItems.value.find(item => (item.product_id || item.id) === sampleId);
          if (itemToRemove) {
            await removeFromCart(itemToRemove.key);
          }
        }
      }
    }, 100);

    // Watch for cart changes and remove ineligible samples
    watch(
      () => [props.cartTotal, addedSamples.value],
      ([newTotal], [oldTotal]) => {
        debouncedRemoveIneligible(newTotal, oldTotal);
      },
      { immediate: false }
    );

    watch(
      () => (props.cartTotal / 100) < activeTierMinPrice.value,
      (isIneligible) => {
        if (isIneligible && showFreeSamples.value) {
          closeFreeSamples();
        }
      }
    );

    // Initialize on mount
    onMounted(async () => {
      freeSamples.value = window.freeSamples || { active: false, heading: '', description: '', tiers: [] };
      await getCartAndUpdateSamples();
      isInitialLoad.value = false;
    });

    // Accordion functions
    const handleFreeSamplesToggle = () => {
      toggleFreeSamples();
      console.log("Free samples accordion toggled. Current value:", showFreeSamples.value);
    };

    // Handle keyboard events for accordion header
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleFreeSamplesToggle();
      }
    };

    // Handle Escape key in accordion panel
    const handlePanelKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeFreeSamples();
        // Return focus to header button
        const headerButton = document.getElementById('free-samples-header');
        if (headerButton) {
          headerButton.focus();
        }
      }
    };

    return {
      freeSamples,
      addedSamples,
      cartItems,
      eligibleTier,
      addedFromEligibleTier,
      canAddMoreSamples,
      sampleStatus,
      eligibleTierProducts,
      productAlreadyAdded,
      getCartAndUpdateSamples,
      removeFromCart,
      notifySampleRemoved,
      isFreeSample,
      addToCart,
      // Global loading state
      isLoading,
      isAnyLoading,
      // Accordion state and functions
      showFreeSamples,
      handleFreeSamplesToggle,
      handleKeyDown,
      handlePanelKeyDown,
      activeTierMinPrice,
      eligibleText,
      freeSampleAddingText,
      freeSampleAddButton,
      amountAwayMessage,
      showAccordionIcons,
      modules,
    };
  },
};
</script>